import React, { createContext, useContext, useState, useEffect, useLayoutEffect } from 'react';

const ThemeContext = createContext();

// Available app themes - 4 themes total
export const APP_THEMES = {
  classic: {
    id: 'classic',
    name: 'Classic',
    description: 'Clean blue & gold design',
    preview: {
      primary: '#1e3a5f',
      secondary: '#d4a853',
      background: '#87CEEB',  // Light sky blue
      card: '#ffffff',
      text: '#000000'  // Black text
    }
  },
  emerald: {
    id: 'emerald',
    name: 'Islamic Emerald',
    description: 'Traditional green & gold',
    preview: {
      primary: '#115e42',
      secondary: '#d4a853',
      background: '#0d4a35',
      card: '#fef9e7'
    }
  },
  royal: {
    id: 'royal',
    name: 'Royal Night',
    description: 'Elegant purple & gold',
    preview: {
      primary: '#5b21b6',
      secondary: '#fbbf24',
      background: '#1a1625',
      card: '#f5f3ff'
    }
  },
  kids: {
    id: 'kids',
    name: 'Kids Paradise',
    description: 'Fun & colorful for children',
    preview: {
      primary: '#ec4899',
      secondary: '#06b6d4',
      background: '#F472B6',  // Darker pink (pink-400)
      card: '#fce7f3',
      text: '#000000'  // Black text
    }
  }
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Light/dark mode
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('noor-mode');
    if (saved) return saved;
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    return 'light';
  });

  // App theme (color scheme)
  const [appTheme, setAppTheme] = useState(() => {
    const saved = localStorage.getItem('noor-app-theme');
    return saved || 'classic';
  });

  // Legacy compatibility
  const theme = mode;
  const setTheme = setMode;

  // QA Bug E — synchronously reconcile the <html> classes with the current
  // theme+mode state BEFORE the browser paints this render. `public/index.html`
  // already runs a pre-hydration script that sets the correct classes for the
  // very first paint; this useLayoutEffect keeps state and DOM in sync on any
  // subsequent theme change without a paint-flash. On Android WebView the
  // previous useEffect fired AFTER the first paint, briefly leaving the DOM
  // authored for e.g. Royal Knight rendered against the default Classic CSS
  // variables — which showed up in Settings and nav menus as "dark text on
  // dark background". Do not downgrade this to useEffect.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    Object.keys(APP_THEMES).forEach(t => {
      root.classList.remove(`theme-${t}`);
    });
    let actualMode = mode;
    if (mode === 'system') {
      actualMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    root.classList.add(`theme-${appTheme}`);
    root.classList.add(actualMode);
  }, [mode, appTheme]);

  // Apply both mode and theme together to ensure proper class combination
  // (persistence + logging pass — no longer owns the paint-critical class
  // application, which is handled by the useLayoutEffect above).
  useEffect(() => {
    localStorage.setItem('noor-mode', mode);
    localStorage.setItem('noor-app-theme', appTheme);
    let actualMode = mode;
    if (mode === 'system') {
      actualMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    console.log('Theme applied:', { appTheme, mode, actualMode, classes: document.documentElement.className });
  }, [mode, appTheme]);

  // Listen to system theme changes
  useEffect(() => {
    if (mode !== 'system') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      const root = document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(e.matches ? 'dark' : 'light');
    };
    
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [mode]);

  // Listen for theme reset events (when trial expires)
  useEffect(() => {
    const handleThemeReset = (event) => {
      const newTheme = event.detail || 'classic';
      console.log('Theme reset to:', newTheme);
      setAppTheme(newTheme);
    };
    
    window.addEventListener('theme-reset', handleThemeReset);
    return () => window.removeEventListener('theme-reset', handleThemeReset);
  }, []);

  const toggleTheme = () => {
    setMode(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const toggleMode = () => {
    setMode(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      setTheme, 
      toggleTheme,
      toggleMode,
      mode,
      setMode,
      appTheme,
      setAppTheme,
      themes: APP_THEMES
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
